import { useEffect, useRef } from "react";
import type { IMessage } from "@stomp/stompjs";
import { useWebSocketClient } from "@/contexts/WebSocketContext";
import { registerTopicHandler } from "@/lib/websocket-topic-registry";

type MessageHandler = (msg: IMessage) => void;

/**
 * Subscribe to a STOMP topic using the shared WebSocketContext client.
 *
 * ### "Not connected yet" handling
 * There is no manual pending-subscription queue here. React's own reactivity
 * replaces it safely:
 *
 *   1. Component mounts before WS connects → `connected = false` →
 *      useEffect returns early (no subscribe).
 *   2. WS connects → `setConnected(true)` in WebSocketContext →
 *      every consumer re-renders → this effect re-runs with
 *      `connected = true` and a valid `client` → subscribes. ✓
 *   3. WS disconnects → `clearAllTopicHandlers()` is called in
 *      WebSocketContext (flushes the module-level registry) →
 *      `connected = false` → this effect re-runs → early return
 *      → cleanup unsubscribes the handler (no-op if registry already cleared). ✓
 *   4. WS auto-reconnects → step 2 repeats. ✓
 *
 * ### Handler stability
 * The handler is kept in a ref so the STOMP callback always invokes the
 * latest closure WITHOUT needing to tear down and recreate the subscription
 * every time the handler identity changes (e.g. due to parent re-renders).
 *
 * @param topic  STOMP destination, e.g. `/topic/conversations/123`.
 *               Pass `null` to temporarily disable the subscription.
 * @param onMessage  Called for every incoming message on the topic.
 * @param key  Optional stable id for the handler inside the registry
 *             (useful when multiple callers share the same topic).
 */
export function useWebSocketChannel(
  topic: string | null,
  onMessage: MessageHandler,
  key?: string
) {
  const { client, connected } = useWebSocketClient();

  // Always keep the latest handler in a ref so the STOMP callback never
  // goes stale, even if onMessage is re-created on every parent render.
  const onMessageRef = useRef<MessageHandler>(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    // Guard: do nothing until the client is connected and we have a topic.
    // When `connected` flips true (or `topic` / `client` changes), this
    // effect re-runs automatically — no manual queue needed.
    if (!client || !connected || !topic) return;

    const { unsubscribe } = registerTopicHandler({
      client,
      topic,
      // Delegate through the ref so the registered STOMP callback is always
      // current without requiring a re-subscribe.
      handler: (msg) => onMessageRef.current(msg),
      clientId: key,
    });

    return () => {
      // Called when topic/client/connected changes or the component unmounts.
      // registerTopicHandler's unsubscribe is safe to call even if
      // clearAllTopicHandlers already removed the entry (it checks internally).
      unsubscribe();
    };
    // Intentionally NOT including `onMessage` — handler updates go through
    // the ref above so we avoid unnecessary re-subscriptions.
  }, [client, connected, topic, key]);
}

export function useWebSocketPublish() {
  const { client, connected } = useWebSocketClient();

  return (destination: string, body: any) => {
    if (!client || !connected) return;
    client.publish({
      destination,
      body: JSON.stringify(body),
    });
  };
}
